import { useMemo, useState } from 'react'
import { Breadcrumb, ProgressIndicator } from '@/components/atoms'
import { PageHeader, SaveCancelFooter } from '@/components/molecules'
import { ActivitySetUp } from '@/containers'
import { isNotEmptyAndDefined } from '@/helpers/shadcn/utils'
import { useAppSelector } from '@/store'
import { resetActivityData, setActivityFormData } from '@/store/slices/activitySlice'
import { setToastNotifications } from '@/store/slices/commonSlice'
import { ACTIVITY_TYPE } from '@/types/Activity'
import { ROUTES } from '@/utils/constants/routesConstants'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import useActivityReview from '@/containers/Activity/ActivityReview/ActivityReview-hook'
import { COUNTRY_CODES_FREQUENCY } from '@/utils/constants/globalConstants'
import { DATE_VALIDATION_TYPE} from '@/utils/constants/commonConstants'
import { getDisplayCurrency, isInProgress, isPublished, flattenSegments } from '@/utils/commons'
import { useActivityValidation } from '@/hooks/useActivityValidation'
import {getModal} from '@/utils/CommonModal'
import { useDatePickerInput } from '@/components/atoms/DatePickerInput/datePickerInput-hook'
const AddActivity = () => {
  const [isCancel, setIsCancel] = useState(false)
  const { formData } = useSelector(state => state.activity)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const countryCode = useAppSelector(state => state.countryCode).countryCode
  const isCountryCodeFrequency = useMemo(() => COUNTRY_CODES_FREQUENCY.includes(countryCode), [countryCode]);
  const localCurrencyCode = getDisplayCurrency(countryCode)
  const { handleDraft: handleReviewDraft,handleValidationModalClose,isNameValid,getActivityName, validateName } = useActivityReview()
  
  const {
    handleActivityDurationValidation,
    resetValidation,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
  } = useActivityValidation()

  const {
    pointsEarned,
    pointsEarnedValid,
    activityType,
    hasEndDate,
    startDate,
    endDate,
    dailyLoginLimit,
    dailyLoginLimitValid,
    allTimeLoginLimit,
    allTimeLoginLimitValid,
    startDateValid,
    endDateValid,
    activityName,
    orderType,
    localCurrency,
    localCurrencyValid,
    numberOfCases,
    numberOfCasesValid,
    receivePoints,
    status
  } = formData

  const isPublishedOrInProgress = isPublished(status) || isInProgress(status)
  const {validateDates: validateStartDate} = useDatePickerInput('startDate',   [
    {type: 'required', msg: ` is required`},
    {type: DATE_VALIDATION_TYPE.START_DATE, msg: ` can't be in the past.`, endDate: endDate},
    {type: DATE_VALIDATION_TYPE.START_DATE_TODAY, msg: `  Target can only start from a date in future`},  ])
  const {validateDates: validateEndDate} = useDatePickerInput('endDate', [
    {type: 'required', msg: ` is required`},
    {type: DATE_VALIDATION_TYPE.START_DATE, msg: ` can't be in the past.`},
    {type: DATE_VALIDATION_TYPE.END_DATE, msg: ` can't be less than Start date.`, startDate: startDate},
  ])
  // Order alignment for Welcome review page
  const isValidStartDate = date => {
    if (!date) return false
    const d = validateStartDate(date)
    return d === undefined || d === null
  }

  const isValidEndDate = date => {
    if (!date) return false
    const d = validateEndDate(date)
    return d === undefined || d === null
  }
  const welcomeActivityOrder = {
    activityType: activityType,
    pointsEarned: pointsEarned,
    hasEndDate: hasEndDate,
    startDate: startDate,
    ...(formData.name && {name: formData.name}),
    ...(formData.activityName && {activityName: formData.activityName}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id}),
    ...(hasEndDate && {endDate: endDate}),
  }

  // Order alignment for Login review page
  const loginActivityOrder = {
    activityType: activityType,
    pointsEarned: pointsEarned,
    ...(dailyLoginLimit && {dailyLoginLimit: dailyLoginLimit}),
    ...(allTimeLoginLimit && {allTimeLoginLimit: allTimeLoginLimit}),
    startDate: startDate,
    endDate: endDate,
    receivePoints: receivePoints,
    ...(formData.name && {name: formData.name}),
    ...(formData.activityName && {activityName: formData.activityName}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id}),
  }

  const pointsOnOrderDeliveryActivityOrder = () => {
    const fieldsArranged = {
      activityType: activityType,
      activityName: activityName,
      orderType: orderType,
      ...(formData.name && {name: formData.name}),
      ...(formData.uuid && {uuid: formData.uuid}),
      ...(formData.status && {status: formData.status}),
      ...(formData.id && {id: formData.id}),
    }
    if (isCountryCodeFrequency) {
      fieldsArranged.numberOfCases = numberOfCases
      fieldsArranged.pointsEarned = pointsEarned
    } else {
      fieldsArranged.localCurrency = localCurrency
      fieldsArranged.pointsEarned = pointsEarned
    }
    fieldsArranged.startDate = startDate
    fieldsArranged.endDate = endDate
    return fieldsArranged
  }

  const handleDraft = event => {
    event.stopPropagation()
    handleReviewDraft()
  }


  const handleNext = async () => {
    const uniqueActivityName = getActivityName()
    // validate name only if it's a new activity as edting name is disabled
    if(formData?.status==null){
      const isValidName = await validateName(uniqueActivityName)
      if (!isValidName) return
    }
    
    // Check if Point Expiration data is valid before proceeding
    if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION && isInvalidPointExpirationData()) {
      // Show validation error
      dispatch(setToastNotifications({
        show: true, 
        message: 'Please enter a valid frequency between 1 and 365 days', 
        type: 'error'
      }))
      return;
    }
    
    const formDataWithOrder = getFormDataWithOrder();
    dispatch(setActivityFormData(formDataWithOrder));
    
    // Skip validation for Point Expiration since API doesn't support it yet
    if (activityType !== ACTIVITY_TYPE.POINT_EXPIRATION) {
      let validationParams = {...formData, activityName: uniqueActivityName}
      const isDurationValid = await handleActivityDurationValidation(validationParams);
      if (!isDurationValid) {
        // Validation failed, toast is already shown by the hook
        return;
      }
    }
    
    // Validation passed - reset any existing toast state and navigate
    resetValidation();
    navigate(ROUTES.ACTIVITY_REVIEW);
  };
  const handleCancel = () => {
    setIsCancel(!isCancel)
  }

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidLoginDataForEdit = ({ validateStartDate = true }) => {
    const isStartDateInvalid = !isNotEmptyAndDefined(startDate)
    const validStartDate = validateStartDate ? isValidStartDate(startDate) : true
    const isEndDateInvalid = !isNotEmptyAndDefined(endDate)
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    const isReceivePointsInvalid = !isNotEmptyAndDefined(receivePoints)

    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsEarnedError = pointsEarnedValid === true
    const hasDailyLoginLimitError = dailyLoginLimitValid === true
    const hasAllTimeLoginLimitError = allTimeLoginLimitValid === true
    // const hasEndDateError = endDateValid === true || endDateValid === undefined
    const validEndDate = isValidEndDate(endDate)
    return (
      !validStartDate ||
      hasPointsEarnedError ||
      hasDailyLoginLimitError ||
      hasAllTimeLoginLimitError ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      isReceivePointsInvalid ||
      isPointsEarnedInvalid ||
      !validEndDate
    )
  }

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidLoginDataForCreation = ({ validateStartDate = true }) => {
    const isStartDateInvalid = !isNotEmptyAndDefined(startDate)
    const validStartDate = validateStartDate ? isValidStartDate(startDate) : true
    const isEndDateInvalid = !isNotEmptyAndDefined(endDate)
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    const isReceivePointsInvalid = !isNotEmptyAndDefined(receivePoints)

    // Stricter validation - check for validation errors AND undefined states
    const hasPointsEarnedError = pointsEarnedValid === true
    const hasDailyLoginLimitError = dailyLoginLimitValid === true
    const hasAllTimeLoginLimitError = allTimeLoginLimitValid === true
    const validEndDate = isValidEndDate(endDate)
    return (
      hasPointsEarnedError ||
      !validStartDate ||
      hasDailyLoginLimitError ||
      hasAllTimeLoginLimitError ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      isReceivePointsInvalid ||
      isPointsEarnedInvalid ||
      !validEndDate
    )
  }

  const isInvalidLoginData = ({ validateStartDate = true }) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode = formData.uuid || formData.status || formData.activityName
    if (isEditMode) {
      return isInvalidLoginDataForEdit({ validateStartDate })
    } else {
      return isInvalidLoginDataForCreation({ validateStartDate })
    }
  }

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidWelcomeDataForEdit = ({ validateStartDate = true }) => {
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    const isStartDateMissing = !isNotEmptyAndDefined(startDate)
    const validStartDate = validateStartDate ? isValidStartDate(startDate) : true
    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid
    const hasStartDateError = startDateValid === true
    const hasEndDateError = endDateValid === true

    if (hasEndDate) {
      const isEndDateMissing = !isNotEmptyAndDefined(endDate)
      return (
        !validStartDate ||
        hasPointsError ||
        hasStartDateError ||
        hasEndDateError ||
        isStartDateMissing ||
        isEndDateMissing
      )
    }

    return !validStartDate || hasPointsError || hasStartDateError || isStartDateMissing
  }

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidWelcomeDataForCreation = ({ validateStartDate = true }) => {
    const isStartDateInvalid = validateStartDate ? validateDate(startDateValid) : true
    const isEndDateInvalid = validateDate(endDateValid)
    const isPointsEarnedInvalid = !isNotEmptyAndDefined(pointsEarned)
    const isStartDateMissing = !isNotEmptyAndDefined(startDate)

    // Check if points earned is invalid (either validation error or empty value)
    const hasPointsError = pointsEarnedValid === true || isPointsEarnedInvalid

    if (hasEndDate) {
      const isEndDateMissing = !isNotEmptyAndDefined(endDate)
      return hasPointsError || isStartDateInvalid || isEndDateInvalid || isStartDateMissing || isEndDateMissing
    }

    return hasPointsError || isStartDateInvalid || isStartDateMissing
  }

  const isInvalidWelcomeData = ({ validateStartDate = true }) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode = formData.activityName || formData.uuid || formData.status

    if (isEditMode) {
      return isInvalidWelcomeDataForEdit({ validateStartDate })
    } else {
      return isInvalidWelcomeDataForCreation({ validateStartDate })
    }
  }

  // Validation for edit scenario - more lenient with undefined validation states
  const isInvalidPointsOrderDeliverForEdit = ({ validateStartDate = true }) => {
    const isStartDateMissing = !isNotEmptyAndDefined(startDate) 
    const validStartDate = validateStartDate ? isValidStartDate(startDate) : true
    const isEndDateMissing = !isNotEmptyAndDefined(endDate)
    const isCurrencyFrequencyValid = isCountryCodeFrequency
      ? numberOfCasesValid === true || !isNotEmptyAndDefined(numberOfCases)
      : localCurrencyValid === true || !isNotEmptyAndDefined(localCurrency)

    // Only check for actual validation errors (true), ignore undefined states
    const hasPointsEarnedError = pointsEarnedValid === true
    const hasStartDateError = startDateValid === true
    const hasEndDateError = endDateValid === true

    return (
      hasPointsEarnedError ||
      !validStartDate ||
      isCurrencyFrequencyValid ||
      !isNotEmptyAndDefined(pointsEarned) ||
      !isNotEmptyAndDefined(activityName) ||
      hasStartDateError ||
      hasEndDateError ||
      isStartDateMissing ||
      isEndDateMissing ||
      !isNotEmptyAndDefined(orderType)
    )
  }

  // Validation for creation scenario - stricter validation including undefined states
  const isInvalidPointsOrderDeliverForCreation = ({ validateStartDate = true }) => {
    const isStartDateInvalid = validateStartDate ? validateDate(startDateValid) : true
    const isEndDateInvalid = !isStartDateInvalid && formData.endDate === undefined
    const isCurrencyFrequencyValid = isCountryCodeFrequency
      ? numberOfCasesValid || !isNotEmptyAndDefined(numberOfCases)
      : localCurrencyValid || !isNotEmptyAndDefined(localCurrency)
    const hasPointsEarnedError = pointsEarnedValid === true
    return (
      hasPointsEarnedError ||
      isCurrencyFrequencyValid ||
      !isNotEmptyAndDefined(pointsEarned) ||
      !isNotEmptyAndDefined(activityName) ||
      isStartDateInvalid ||
      isEndDateInvalid ||
      !isNotEmptyAndDefined(orderType)
    )
  }

  const isInvalidPointsOrderDeliver = ({ validateStartDate = true }) => {
    // Determine if we're in edit mode by checking if activity has existing data
    const isEditMode = formData.activityName || formData.uuid || formData.status

    if (isEditMode) {
      return isInvalidPointsOrderDeliverForEdit({ validateStartDate })
    } else {
      return isInvalidPointsOrderDeliverForCreation({ validateStartDate })
    }
  }

  const validateDate = dateValid => {
    // Only return true (invalid) if there's an actual validation error
    // undefined means not validated yet, which should be treated as valid for pre-filled data
    return dateValid === true
  }

  // Validation for Point Expiration activity
  const isInvalidPointExpirationData = () => {
    const isNoOfDaysInvalid = !isNotEmptyAndDefined(formData?.noOfDays)
    
    // Check if noOfDays is within valid range (1-365)
    const noOfDaysValue = parseInt(formData?.noOfDays, 10)
    const isNoOfDaysOutOfRange = isNaN(noOfDaysValue) || noOfDaysValue < 1 || noOfDaysValue > 365
    
    return isNoOfDaysInvalid || isNoOfDaysOutOfRange
  }

  const validateFormData = ({ validateStartDate = true }) => {
  
    if (formData.uuid || formData.status) return false
    
    const noSegmentsSelected =
      formData?.allCustomers === false && (!Array.isArray(formData?.segments) || flattenSegments(formData.segments).length === 0)
    if (noSegmentsSelected) {
      return true
    }
    if (activityType === ACTIVITY_TYPE.LOGIN) {
      return isInvalidLoginData({ validateStartDate })
    } else if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return isInvalidWelcomeData({ validateStartDate })
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return isInvalidPointsOrderDeliver({ validateStartDate })
    } else if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
      return isInvalidPointExpirationData()
    } else if (handleHideSaveDraft()) {
      return true
    }
  }

  const buildCommonFields = fd => ({
    name: fd.name,
    status: fd.status,
    uuid: fd.uuid,
    id: fd.id,
    ...(fd?.allCustomers === false && Array.isArray(fd?.segments) && flattenSegments(fd.segments).length > 0
      ? { segments: flattenSegments(fd.segments) }
      : {}),
    allCustomers: fd?.allCustomers,
    excludeSegments: fd?.excludeSegments,
  })

  // Point Expiration activity order
  const pointExpirationActivityOrder = {
    activityType: activityType,
    noOfDays: formData.noOfDays,
    ...(formData.name && {name: formData.name}),
    ...(formData.activityName && {activityName: formData.activityName}),
    ...(formData.uuid && {uuid: formData.uuid}),
    ...(formData.status && {status: formData.status}),
    ...(formData.id && {id: formData.id}),
  }

  const getFormDataWithOrder = () => {
    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return {
        ...welcomeActivityOrder,
        ...buildCommonFields(formData),
      }
    } else if (activityType === ACTIVITY_TYPE.LOGIN) {
      return {
        ...loginActivityOrder,
        ...buildCommonFields(formData),
      }
    } else if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return {
        ...pointsOnOrderDeliveryActivityOrder(),
        ...buildCommonFields(formData),
      }
    } else if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
      return {
        ...pointExpirationActivityOrder,
        ...buildCommonFields(formData),
      }
    }
  }

  const onClickPrimaryButton = () => {
    handleResetFormData()
    navigate(ROUTES.ACTIVITIES)
  }

  const onClickSecondaryButton = () => {
    setIsCancel(!isCancel)
  }

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false,
    }
    dispatch(resetActivityData(defaultFormData))
  }

  const handleHideSaveDraft = () => {
    return formData.activityType === '' || formData.activityType === undefined
  }

  const getCustomModal = (type) => {
      switch(type) {
        case "CANCEL_MODAL":
          return getModal({
            title: "Are you sure you want to cancel?",
            text: 'Your work will not be saved if you click Yes. To continue editing and go back to the page, click NO',
            showPrimaryButton: true, 
            primaryButtonText: "Yes",
            onClickPrimaryButton: onClickPrimaryButton, 
            showSecondaryButton:   true,
            secondaryButtonText: "No",
            onClickSecondaryButton: onClickSecondaryButton, 
            showModal: isCancel, 
            onCloseModal: handleCancel, 
            hideCloseButton: false
          })
        case "NAME_VALIDATION_MODAL":
          return getModal({
            title: "Duplicate Name Detected",
            text: (
              <>
                It looks like there's already an Activity with this name.<br />
                To avoid confusion, please enter a unique name before continuing.
              </>
                ),
            showPrimaryButton: true, 
            primaryButtonText: "Edit Name",
            onClickPrimaryButton: handleValidationModalClose, 
            showSecondaryButton:   false,
            showModal: !isNameValid, 
            onCloseModal: handleValidationModalClose, 
            hideCloseButton: true
          })
        default:
          return null;
      }
  }

  return (
    <>
      <Breadcrumb
        uiSchema={{
          isDark: true,
          className: 'mb-5',
          breadcrumbs: [
            {
              label: '',
              href: '/',
            },
            {
              label: 'Loyalty Campaigns',
              href: '/rewards/activities',
            },
            {
              label: 'Add Activity',
              href: '/rewards/activities',
            },
          ],
        }}
        id={'aa_bradcrumb'}
      />

      <PageHeader title={'Add Activity'} />

      <ProgressIndicator
        currentStep={1}
        completed={false}
        steps={2}
        titles={[{title: 'Setup'}, {title: 'Review'}]}
        onStepClick={step => {
          if (step === 2) {
            navigate(ROUTES.ACTIVITY_REVIEW)
          }
        }}
      />
      <br />
      <ActivitySetUp 
        localCurrencyCode={localCurrencyCode} 
        showToast={showToast}
        toastMessage={toastMessage}
        handleToastClose={handleToastClose}
        handleViewActivity={handleViewActivity}
      />
      {!isNameValid && getCustomModal("NAME_VALIDATION_MODAL")}
      {isCancel && getCustomModal("CANCEL_MODAL")}
      <SaveCancelFooter
        handleCancel={handleCancel}
        nextAction={handleNext}
        draftAction={handleDraft}
        FormError={validateFormData({ validateStartDate: !isPublishedOrInProgress  })}
        hideSaveDraft={handleHideSaveDraft()}
        disableDraft={isPublishedOrInProgress}
      />
    </>
  )
}
export default AddActivity
